import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { doDeleteOrders, doGetOrders } from '../modules/orders/stores/thunk';
import { selectAllOrders } from '../modules/orders/stores/slice';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DATE_BACKEND_FORMAT, ORDER_STATUSES, PAGE_SIZE } from '../constants';
import OrdersTable from '../components/Tables/OrdersTable';
import { Alert, Badge, Button, Input, message, Popconfirm, Popover, Segmented } from 'antd';
import { DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { restoreOrder, permanentDeleteOrders, emptyTrash } from '../modules/orders/api';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import OrdersFilter from '../components/Forms/Filters/OrdersFilter';
import RouteDateQuickFilter from '../components/Forms/Filters/RouteDateQuickFilter';
import ActiveOrderFiltersBar from '../components/Forms/Filters/ActiveOrderFiltersBar';
import { getDrivers } from '../modules/drivers/api';
import moment from 'moment';
import { socket } from '../utils/socket';
import { downloadOrdersCsv } from '../utils/orderCsvExport';

const OrdersPage = () => {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  const { isFetching, metadata, action, status } = useSelector((state) => state.order);
  const orders = useSelector((state) => selectAllOrders(state));

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const tableConfigRef = useRef({});
  const [isActionOpen, setIsActionOpen] = useState(false);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const query = Object.fromEntries(searchParams);
  // #173 — the driver filter chip shows the name, not "#id".
  const [driversById, setDriversById] = useState({});
  useEffect(() => {
    if (!query.driverId || driversById[query.driverId]) return;
    getDrivers({ take: 1000 }).then(({ response }) => {
      setDriversById(Object.fromEntries((response?.drivers || []).map((d) => [String(d.id), d.name])));
    });
  }, [query.driverId, driversById]);
  const currentPage = query.current || 1;
  const drawerPageSize = tableConfigRef.current?.pageSize;
  const itemsPerPage = query.pageSize || drawerPageSize || PAGE_SIZE;
  const pagination = {
    current: currentPage || 1,
    pageSize: itemsPerPage,
    locale: { items_per_page: tc('itemsPerPage', { entity: t('title') }) },
    total: metadata.totalItems || orders.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  useEffect(() => {
    dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  useEffect(() => {
    if (['deleteOrders']?.includes(action) && status === 'succeeded')
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
  }, [action, status]);

  // Refresh the list when the backend reports geocoding done. Sep 25 2026:
  // this effect used to depend on `query` — a NEW object every render — so
  // it registered one more listener per render, and its cleanup only called
  // socket.disconnect(), which does NOT remove listeners (same trap as
  // OVO-005). Every `orders.geocoding.completed` then fired one GET /orders
  // per accumulated listener: ~2,800 requests in 3 minutes during a partner
  // intake burst, which exhausted the prod DB pool and made the partner's
  // uploads fail. Now: ONE named handler per mount, removed on unmount; it
  // reads the current filters from a ref, coalesces bursts (one refetch per
  // quiet 1.5 s) and skips hidden tabs until they are visible again. The
  // shared socket singleton is never disconnected from here.
  const refetchArgsRef = useRef(null);
  refetchArgsRef.current = { page: currentPage, take: itemsPerPage, ...query };
  useEffect(() => {
    let timer = null;
    let pendingWhileHidden = false;
    const refetch = () => {
      timer = null;
      if (document.visibilityState === 'hidden') {
        pendingWhileHidden = true;
        return;
      }
      dispatch(doGetOrders({ ...refetchArgsRef.current }));
    };
    const handleOrders = (data) => {
      if (data?.event !== 'orders.geocoding.completed') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(refetch, 1500);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && pendingWhileHidden) {
        pendingWhileHidden = false;
        refetch();
      }
    };
    socket.connect();
    socket.on('orders', handleOrders);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off('orders', handleOrders);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectChange = (newSelectedRowKeys) => {
    if (newSelectedRowKeys.length === 0 && isActionOpen) setIsActionOpen(false);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleClickDeleteOrders = () => {
    if (isActionOpen) setIsActionOpen(false);
    const orderIdsRemove = orders
      .filter(
        (o) =>
          selectedRowKeys?.includes(o.orderCode) &&
          [ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR, ORDER_STATUSES.UNASSIGNED]?.includes(
            o.status,
          ),
      )
      ?.map((o) => o.orderCode);

    if (orderIdsRemove.length !== selectedRowKeys.length) {
      const errorIds = [];
      selectedRowKeys.forEach((orderCode) => {
        if (!orderIdsRemove?.includes(orderCode)) errorIds.push(orderCode);
      });
      const orderCodesError = orders.filter((o) => errorIds?.includes(o.orderCode))?.map((o) => o.orderCode);
      messageApi.open({
        content: t('cannotDeleteOrders', { orderCodes: orderCodesError.join(', ') }),
        type: 'error',
        style: { marginTop: '52px' },
      });
    } else {
      dispatch(doDeleteOrders({ orderCodes: selectedRowKeys }));
    }
  };

  const handleExportOrders = async () => {
    if (isActionOpen) setIsActionOpen(false);
    const selectedOrders = orders.filter((o) => selectedRowKeys?.includes(o.orderCode));
    if (!selectedOrders.length) return;
    await downloadOrdersCsv(selectedOrders);
    messageApi.open({
      type: 'success',
      style: { marginTop: '52px' },
      content: t('exportSuccess', { count: selectedOrders.length }),
    });
  };

  const contentPopoverAction = () => {
    // OVO-116 — in the trash view, bulk action is Restore (no delete).
    if (isTrashView) {
      return (
        <div className="buttons">
          <Button type="link" onClick={handleExportOrders}>
            {t('exportCsv')}
          </Button>
          <Button type="link" onClick={handleBulkRestore}>
            {t('restoreSelected')}
          </Button>
          <Popconfirm
            title={t('deletePermanently')}
            description={t('deletePermanentlyBulkConfirm', { count: selectedRowKeys.length })}
            onConfirm={handleBulkPermanentDelete}
            okText={t('deletePermanently')}
            cancelText={tc('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger>
              {t('deletePermanently')}
            </Button>
          </Popconfirm>
        </div>
      );
    }
    return (
      <div className="buttons">
        <Button type="link" onClick={handleExportOrders}>
          {t('exportCsv')}
        </Button>
        <Popconfirm
          title={tc('areYouSureDelete')}
          onConfirm={() => handleClickDeleteOrders()}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button type="link">{tc('delete')}</Button>
        </Popconfirm>
      </div>
    );
  };

  const onClickAction = () => {
    selectedRowKeys.length > 0 && setIsActionOpen(!isActionOpen);
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetOrders({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  // Quick date chips (Today / Tomorrow / This week …) — the same component
  // as the Routes page, mapped onto the orders' delivery-date window. Merges
  // into the current query so every other filter survives.
  const handleQuickDate = ({ dateFrom, dateTo }) => {
    const next = { ...query, current: 1 };
    if (dateFrom) next.deliveryDateFrom = dateFrom;
    else delete next.deliveryDateFrom;
    if (dateTo) next.deliveryDateTo = dateTo;
    else delete next.deliveryDateTo;
    handleFilter(next, true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleTableChange = (paginationParams, _filters, sorter) => {
    const newParams = { ...query, ...paginationParams };

    if (sorter && sorter.field && sorter.order) {
      newParams.sortField = sorter.field;
      newParams.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      delete newParams.sortField;
      delete newParams.sortOrder;
    }

    handleFilter(newParams, true);
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    // OVO-242 — MERGE with the current query instead of replacing it: passing
    // only {q} wiped `onlyDeleted` (Enter in the Trash bounced you back to
    // Active) and every other active filter (status, partner, dates…).
    handleFilter({ ...query, q: searchValue || '', current: 1 }, true);
  };

  const DELETABLE_STATUSES = [
    ORDER_STATUSES.DRAFT,
    ORDER_STATUSES.GEOCODING,
    ORDER_STATUSES.ERROR,
    ORDER_STATUSES.UNASSIGNED,
  ];

  const handleDeleteOrder = async (record) => {
    try {
      await dispatch(doDeleteOrders({ orderCodes: [record.orderCode] })).unwrap();
      message.success(t('orderDeleted', { orderCode: record.orderCode }));
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDelete'));
    }
  };

  // OVO-116 — soft-delete "trash" view. Delete now moves an order to the
  // trash (recoverable) instead of removing it; `onlyDeleted=true` lists the
  // trash and a Restore action brings orders back to their previous status.
  const isTrashView = query.onlyDeleted === 'true';

  const handleToggleTrash = (val) => {
    setSelectedRowKeys([]);
    const next = { ...query, current: 1 };
    if (val === 'trash') next.onlyDeleted = 'true';
    else delete next.onlyDeleted;
    handleFilter(next, true);
  };

  const handleRestoreOrder = async (record) => {
    try {
      await restoreOrder(record.orderCode);
      message.success(t('orderRestored', { orderCode: record.orderCode }));
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToRestore'));
    }
  };

  const handleBulkRestore = async () => {
    if (isActionOpen) setIsActionOpen(false);
    try {
      await Promise.all(selectedRowKeys.map((code) => restoreOrder(code)));
      message.success(t('ordersRestored', { count: selectedRowKeys.length }));
      setSelectedRowKeys([]);
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToRestore'));
    }
  };

  // OVO-116 — PERMANENT delete (real DB removal) from the trash. Irreversible.
  const handlePermanentDeleteOrder = async (record) => {
    try {
      await permanentDeleteOrders([record.orderCode]);
      message.success(t('orderPermanentlyDeleted', { orderCode: record.orderCode }));
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDeletePermanently'));
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (isActionOpen) setIsActionOpen(false);
    try {
      await permanentDeleteOrders(selectedRowKeys);
      message.success(t('ordersPermanentlyDeleted', { count: selectedRowKeys.length }));
      setSelectedRowKeys([]);
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDeletePermanently'));
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const { response } = await emptyTrash();
      message.success(t('emptyTrashSuccess', { count: response?.deleted ?? 0 }));
      setSelectedRowKeys([]);
      dispatch(doGetOrders({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDeletePermanently'));
    }
  };

  const withAction = (record) => {
    // OVO-116 — in the trash view the only per-row action is Restore.
    if (isTrashView) {
      return (
        <div className="actions">
          <Popconfirm
            title={t('restoreOrder')}
            description={t('restoreOrderConfirm', { orderCode: record.orderCode })}
            onConfirm={() => handleRestoreOrder(record)}
            okText={t('restore')}
            cancelText={tc('cancel')}
          >
            <Button type="link" className="more" title={t('restore')}>
              <UndoOutlined />
            </Button>
          </Popconfirm>
          <Popconfirm
            title={t('deletePermanently')}
            description={t('deletePermanentlyConfirm', { orderCode: record.orderCode })}
            onConfirm={() => handlePermanentDeleteOrder(record)}
            okText={t('deletePermanently')}
            cancelText={tc('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" className="more" danger title={t('deletePermanently')}>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </div>
      );
    }
    const isDeletable = DELETABLE_STATUSES.includes(record.status);
    return (
      <div className="actions">
        {isDeletable ? (
          <Popconfirm
            title={t('deleteOrder')}
            description={t('deleteOrderConfirm', { orderCode: record.orderCode })}
            onConfirm={() => handleDeleteOrder(record)}
            okText={tc('delete')}
            cancelText={tc('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/more.svg" />
            </Button>
          </Popconfirm>
        ) : (
          <Button type="link" className="more" disabled>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        )}
        {![ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR].includes(record.status) && (
          <Link to={`/admin/route-plans?date=${moment(record.deliveryDate).format(DATE_BACKEND_FORMAT)}`}>
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/route-plans.svg" />
            </Button>
          </Link>
        )}
      </div>
    );
  };

  const activeFilterCount = [
    query.name,
    query.pickupName,
    query.routeId,
    query.partnerOrderRef,
    query.orderRef,
    query.status,
    query.deliveryDateFrom || query.deliveryDateTo,
    query.riderId,
    query.partnerId,
    query.orderType,
    query.issueTypes,
    query.partnerItemTypeIds, // OVO-019
    query.siteId, // OVO-101
  ].filter(Boolean).length;

  return (
    <div className="OrdersPage Content">
      {contextHolder}
      <div className="ContentHeader">
        <div className="HeaderTitle">{t('title')}</div>
        <div className="HeaderToolBar">
          <div className="ToolBarButtons">
            <Link to="/admin/orders/new">
              <Button className="CreateButton ButtonPrimary" type="link" size="small">
                {t('addSingleOrder')}
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </Link>
            <Link to="/admin/orders/upload">
              <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                {t('uploadOrder')}
                <ReactSVG className="icon" src="/icons/upload.svg" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      {/* OVO-242 UX — unmistakable Trash context: the toggle alone was too
          subtle, operators confused deleted orders with active ones. */}
      {isTrashView && (
        <Alert
          type="warning"
          showIcon
          icon={<span style={{ fontSize: 18 }}>🗑</span>}
          message={<strong>{t('trashBannerTitle')}</strong>}
          description={t('trashBannerDesc')}
          style={{ margin: '0 0 12px 0', border: '1px solid #ffbb96', background: '#fff2e8' }}
        />
      )}
      <div
        className="riders-header-table page-header-table"
        style={isTrashView ? { background: '#fff2e8', borderRadius: 8, padding: 8 } : undefined}
      >
        <div className="search">
          <Input
            onChange={handleChangeSearch}
            onBlur={handleSearch}
            placeholder={tc('search')}
            onKeyDown={handleKeyDown}
            suffix={<img src="/icons/search.svg" />}
            disabled={isFetching}
            value={searchValue}
          />
        </div>
        <div className="quick">
          <RouteDateQuickFilter
            dateFrom={query.deliveryDateFrom}
            dateTo={query.deliveryDateTo}
            onChange={handleQuickDate}
          />
        </div>
        <div className="buttons">
          {/* OVO-116 — Active / Trash view toggle (soft-delete recovery). */}
          <Segmented
            value={isTrashView ? 'trash' : 'active'}
            onChange={handleToggleTrash}
            options={[
              { label: t('activeOrders'), value: 'active' },
              { label: '🗑 ' + t('trash'), value: 'trash' },
            ]}
          />
          {/* OVO-116 — empty the whole trash (permanent, irreversible). */}
          {isTrashView && (
            <Popconfirm
              title={t('emptyTrash')}
              description={t('emptyTrashConfirm')}
              onConfirm={handleEmptyTrash}
              okText={t('emptyTrash')}
              cancelText={tc('cancel')}
              okButtonProps={{ danger: true }}
            >
              <Button className="FilterButton ButtonFilter" danger>
                <DeleteOutlined /> {t('emptyTrash')}
              </Button>
            </Popconfirm>
          )}
          {selectedRowKeys.length > 0 && (
            <Popover
              open={selectedRowKeys?.length > 0 && isActionOpen}
              content={contentPopoverAction}
              overlayClassName="OrdersPagePopoverAction"
              placement="bottom"
            >
              <Button className="FilterButton ButtonFilter" onClick={onClickAction}>
                {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                {tc('action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
              </Button>
            </Popover>
          )}

          <Badge count={activeFilterCount} size="small" offset={[-5, 0]}>
            <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(true)}>
              {tc('filter')}
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </Badge>
          <Button
            type="text"
            onClick={() => tableConfigRef.current?.openDrawer?.()}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <ReactSVG className="icon" src="/icons/settings.svg" />
          </Button>
        </div>
      </div>
      <OrdersFilter open={isFilterOpen} onClose={() => setIsFilterOpen(false)} query={query} onApply={handleFilter} />
      {/* OVO-101 — visible chips for every active filter. Driven by the
          same handleFilter the drawer uses, so removing a chip
          immediately refetches the orders. partnerId is read from the
          query so siteId chips can resolve the site name on the fly. */}
      <ActiveOrderFiltersBar
        query={query}
        onChange={handleFilter}
        partnerId={query.partnerId}
        driversById={driversById}
      />
      <div className="ContentMain">
        <OrdersTable
          dataSource={orders}
          pagination={pagination}
          withAction={withAction}
          rowKey="orderCode"
          isFetching={false}
          onSelectChange={onSelectChange}
          onChange={handleTableChange}
          configRef={tableConfigRef}
          onPageSizeChange={(size) => {
            handleTableChange({ pageSize: size, current: 1 }, null, null);
          }}
        />
      </div>
    </div>
  );
};

export default OrdersPage;
